import * as z from 'zod';

export const GetReviewRequest = z.object({
  reviewId: z
    .string()
    .describe('Review ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      returnPrivateReviews: z
        .boolean()
        .describe('Whether to return unpublished reviews.\n\nDefault: `false`')
        .optional(),
    })
    .describe('Information about the reviews to retrieve.')
    .optional(),
});
export const GetReviewResponse = z.object({
  namespace: z
    .string()
    .describe(
      'Review namespace.\n\nSupported values:\n+ Wix Stores: `stores`\n\nCurrently, only Wix Stores is fully integrated with the Wix Reviews app.'
    )
    .optional(),
  entityId: z
    .string()
    .describe(
      'ID of the entity to review. For example, a Wix Stores product ID.'
    )
    .max(36)
    .optional(),
  _id: z
    .string()
    .describe('Review ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  content: z
    .object({
      title: z
        .string()
        .describe('Review title.')
        .max(3000)
        .optional()
        .nullable(),
      body: z
        .string()
        .describe('Review body.\n\nMax: 3,000 characters')
        .max(3000)
        .optional()
        .nullable(),
      media: z
        .array(
          z.intersection(
            z.object({}),
            z.xor([
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
              }),
              z.object({
                video: z.never().optional(),
                image: z.string().describe('Image media item.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z.string().describe('Video media item.'),
              }),
            ])
          )
        )
        .max(10)
        .optional(),
      rating: z
        .number()
        .int()
        .describe('Rating of the review.\n\nMin: `1` <br />\nMax: `5`')
        .optional(),
    })
    .describe('Review content.')
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time the review was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the review was updated.')
    .optional()
    .nullable(),
  author: z
    .object({
      contactId: z
        .string()
        .describe('Contact ID of the author.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      authorName: z
        .string()
        .describe('Display name of the author.')
        .max(100)
        .optional()
        .nullable(),
    })
    .describe('Author of the review.')
    .optional(),
  reply: z
    .object({
      message: z.string().describe('Reply content.').max(1000).optional(),
      _createdDate: z
        .date()
        .describe('Date and time the reply was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the reply was updated.')
        .optional()
        .nullable(),
    })
    .describe('Reply to the review.')
    .optional(),
  foundHelpful: z
    .number()
    .int()
    .describe('Number of site visitors who found the review helpful.')
    .optional(),
  foundUnhelpful: z
    .number()
    .int()
    .describe('Number of site visitors who found the review unhelpful.')
    .optional(),
  helpfulness: z
    .number()
    .int()
    .describe(
      'Helpfulness score.\n\nCalculated by subtracting `foundUnhelpful` from `foundHelpful`.'
    )
    .optional(),
  moderation: z
    .object({
      moderationStatus: z
        .enum(['UNKNOWN', 'APPROVED', 'IN_MODERATION', 'REJECTED', 'SUBMITTED'])
        .optional(),
      manuallyChanged: z
        .boolean()
        .describe(
          'Indicates whether a moderator manually changed the `moderationStatus` of the review.\n\nIf the `moderationStatus` changed automatically because the review\npassed the moderation rules or if moderation is turned off, this field is set to `false`.'
        )
        .optional(),
    })
    .describe('Moderation status of the review.')
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the review is updated.\nTo prevent conflicting changes,\nthe current `revision` must be passed when updating the review.\nIgnored when creating a review.'
    )
    .optional()
    .nullable(),
  verified: z
    .boolean()
    .describe('Whether the review has been verified.')
    .optional(),
  origin: z
    .object({
      type: z
        .enum(['UNKNOWN', 'ORGANIC', 'APP'])
        .describe('Origin type.')
        .optional(),
      appId: z
        .string()
        .describe(
          'The app ID of the origin for the review.\nOnly available when the `origin.type` is `"APP"`.'
        )
        .optional()
        .nullable(),
    })
    .describe('Origin of a review.')
    .optional(),
  reviewDate: z
    .date()
    .describe(
      'Date and time when the review was written.\nThis should match `createdDate`, except for reviews imported from another system.'
    )
    .optional()
    .nullable(),
  relevanceScore: z
    .number()
    .describe(
      "Platform-calculated score of review's relevance to the item.\nUsed to sort reviews by relevance."
    )
    .optional()
    .nullable(),
  publishCount: z
    .number()
    .int()
    .describe('Number of times a review has been published.')
    .optional(),
});
export const CreateReviewRequest = z.object({
  review: z
    .object({
      namespace: z
        .string()
        .describe(
          'Review namespace.\n\nSupported values:\n+ Wix Stores: `stores`\n\nCurrently, only Wix Stores is fully integrated with the Wix Reviews app.'
        ),
      entityId: z
        .string()
        .describe(
          'ID of the entity to review. For example, a Wix Stores product ID.'
        )
        .max(36),
      _id: z
        .string()
        .describe('Review ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      content: z
        .object({
          title: z
            .string()
            .describe('Review title.')
            .max(3000)
            .optional()
            .nullable(),
          body: z
            .string()
            .describe('Review body.\n\nMax: 3,000 characters')
            .max(3000)
            .optional()
            .nullable(),
          media: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                  }),
                  z.object({
                    video: z.never().optional(),
                    image: z.string().describe('Image media item.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.string().describe('Video media item.'),
                  }),
                ])
              )
            )
            .max(10)
            .optional(),
          rating: z
            .number()
            .int()
            .describe('Rating of the review.\n\nMin: `1` <br />\nMax: `5`'),
        })
        .describe('Review content.'),
      _createdDate: z
        .date()
        .describe('Date and time the review was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the review was updated.')
        .optional()
        .nullable(),
      author: z
        .object({
          contactId: z
            .string()
            .describe('Contact ID of the author.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          authorName: z
            .string()
            .describe('Display name of the author.')
            .max(100)
            .optional()
            .nullable(),
        })
        .describe('Author of the review.')
        .optional(),
      reply: z
        .object({
          message: z.string().describe('Reply content.').max(1000).optional(),
          _createdDate: z
            .date()
            .describe('Date and time the reply was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the reply was updated.')
            .optional()
            .nullable(),
        })
        .describe('Reply to the review.')
        .optional(),
      foundHelpful: z
        .number()
        .int()
        .describe('Number of site visitors who found the review helpful.')
        .optional(),
      foundUnhelpful: z
        .number()
        .int()
        .describe('Number of site visitors who found the review unhelpful.')
        .optional(),
      helpfulness: z
        .number()
        .int()
        .describe(
          'Helpfulness score.\n\nCalculated by subtracting `foundUnhelpful` from `foundHelpful`.'
        )
        .optional(),
      moderation: z
        .object({
          moderationStatus: z
            .enum([
              'UNKNOWN',
              'APPROVED',
              'IN_MODERATION',
              'REJECTED',
              'SUBMITTED',
            ])
            .optional(),
          manuallyChanged: z
            .boolean()
            .describe(
              'Indicates whether a moderator manually changed the `moderationStatus` of the review.\n\nIf the `moderationStatus` changed automatically because the review\npassed the moderation rules or if moderation is turned off, this field is set to `false`.'
            )
            .optional(),
        })
        .describe('Moderation status of the review.')
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the review is updated.\nTo prevent conflicting changes,\nthe current `revision` must be passed when updating the review.\nIgnored when creating a review.'
        )
        .optional()
        .nullable(),
      verified: z
        .boolean()
        .describe('Whether the review has been verified.')
        .optional(),
      origin: z
        .object({
          type: z.enum(['UNKNOWN', 'ORGANIC', 'APP']).optional(),
          appId: z
            .string()
            .describe(
              'The app ID of the origin for the review.\nOnly available when the `origin.type` is `"APP"`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Origin of a review.')
        .optional(),
      reviewDate: z
        .date()
        .describe(
          'Date and time when the review was written.\nThis should match `createdDate`, except for reviews imported from another system.'
        )
        .optional()
        .nullable(),
      relevanceScore: z
        .number()
        .describe(
          "Platform-calculated score of review's relevance to the item.\nUsed to sort reviews by relevance."
        )
        .optional()
        .nullable(),
      publishCount: z
        .number()
        .int()
        .describe('Number of times a review has been published.')
        .optional(),
    })
    .describe('Review data.'),
});
export const CreateReviewResponse = z.object({
  namespace: z
    .string()
    .describe(
      'Review namespace.\n\nSupported values:\n+ Wix Stores: `stores`\n\nCurrently, only Wix Stores is fully integrated with the Wix Reviews app.'
    )
    .optional(),
  entityId: z
    .string()
    .describe(
      'ID of the entity to review. For example, a Wix Stores product ID.'
    )
    .max(36)
    .optional(),
  _id: z
    .string()
    .describe('Review ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  content: z
    .object({
      title: z
        .string()
        .describe('Review title.')
        .max(3000)
        .optional()
        .nullable(),
      body: z
        .string()
        .describe('Review body.\n\nMax: 3,000 characters')
        .max(3000)
        .optional()
        .nullable(),
      media: z
        .array(
          z.intersection(
            z.object({}),
            z.xor([
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
              }),
              z.object({
                video: z.never().optional(),
                image: z.string().describe('Image media item.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z.string().describe('Video media item.'),
              }),
            ])
          )
        )
        .max(10)
        .optional(),
      rating: z
        .number()
        .int()
        .describe('Rating of the review.\n\nMin: `1` <br />\nMax: `5`')
        .optional(),
    })
    .describe('Review content.')
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time the review was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the review was updated.')
    .optional()
    .nullable(),
  author: z
    .object({
      contactId: z
        .string()
        .describe('Contact ID of the author.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      authorName: z
        .string()
        .describe('Display name of the author.')
        .max(100)
        .optional()
        .nullable(),
    })
    .describe('Author of the review.')
    .optional(),
  reply: z
    .object({
      message: z.string().describe('Reply content.').max(1000).optional(),
      _createdDate: z
        .date()
        .describe('Date and time the reply was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the reply was updated.')
        .optional()
        .nullable(),
    })
    .describe('Reply to the review.')
    .optional(),
  foundHelpful: z
    .number()
    .int()
    .describe('Number of site visitors who found the review helpful.')
    .optional(),
  foundUnhelpful: z
    .number()
    .int()
    .describe('Number of site visitors who found the review unhelpful.')
    .optional(),
  helpfulness: z
    .number()
    .int()
    .describe(
      'Helpfulness score.\n\nCalculated by subtracting `foundUnhelpful` from `foundHelpful`.'
    )
    .optional(),
  moderation: z
    .object({
      moderationStatus: z
        .enum(['UNKNOWN', 'APPROVED', 'IN_MODERATION', 'REJECTED', 'SUBMITTED'])
        .optional(),
      manuallyChanged: z
        .boolean()
        .describe(
          'Indicates whether a moderator manually changed the `moderationStatus` of the review.\n\nIf the `moderationStatus` changed automatically because the review\npassed the moderation rules or if moderation is turned off, this field is set to `false`.'
        )
        .optional(),
    })
    .describe('Moderation status of the review.')
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the review is updated.\nTo prevent conflicting changes,\nthe current `revision` must be passed when updating the review.\nIgnored when creating a review.'
    )
    .optional()
    .nullable(),
  verified: z
    .boolean()
    .describe('Whether the review has been verified.')
    .optional(),
  origin: z
    .object({
      type: z
        .enum(['UNKNOWN', 'ORGANIC', 'APP'])
        .describe('Origin type.')
        .optional(),
      appId: z
        .string()
        .describe(
          'The app ID of the origin for the review.\nOnly available when the `origin.type` is `"APP"`.'
        )
        .optional()
        .nullable(),
    })
    .describe('Origin of a review.')
    .optional(),
  reviewDate: z
    .date()
    .describe(
      'Date and time when the review was written.\nThis should match `createdDate`, except for reviews imported from another system.'
    )
    .optional()
    .nullable(),
  relevanceScore: z
    .number()
    .describe(
      "Platform-calculated score of review's relevance to the item.\nUsed to sort reviews by relevance."
    )
    .optional()
    .nullable(),
  publishCount: z
    .number()
    .int()
    .describe('Number of times a review has been published.')
    .optional(),
});
export const BulkCreateReviewRequest = z.object({
  reviews: z
    .array(
      z.object({
        namespace: z
          .string()
          .describe(
            'Review namespace.\n\nSupported values:\n+ Wix Stores: `stores`\n\nCurrently, only Wix Stores is fully integrated with the Wix Reviews app.'
          ),
        entityId: z
          .string()
          .describe(
            'ID of the entity to review. For example, a Wix Stores product ID.'
          )
          .max(36),
        _id: z
          .string()
          .describe('Review ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        content: z
          .object({
            title: z
              .string()
              .describe('Review title.')
              .max(3000)
              .optional()
              .nullable(),
            body: z
              .string()
              .describe('Review body.\n\nMax: 3,000 characters')
              .max(3000)
              .optional()
              .nullable(),
            media: z
              .array(
                z.intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      image: z.never().optional(),
                      video: z.never().optional(),
                    }),
                    z.object({
                      video: z.never().optional(),
                      image: z.string().describe('Image media item.'),
                    }),
                    z.object({
                      image: z.never().optional(),
                      video: z.string().describe('Video media item.'),
                    }),
                  ])
                )
              )
              .max(10)
              .optional(),
            rating: z
              .number()
              .int()
              .describe('Rating of the review.\n\nMin: `1` <br />\nMax: `5`')
              .optional(),
          })
          .describe('Review content.'),
        _createdDate: z
          .date()
          .describe('Date and time the review was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the review was updated.')
          .optional()
          .nullable(),
        author: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID of the author.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            authorName: z
              .string()
              .describe('Display name of the author.')
              .max(100)
              .optional()
              .nullable(),
          })
          .describe('Author of the review.'),
        reply: z
          .object({
            message: z.string().describe('Reply content.').max(1000).optional(),
            _createdDate: z
              .date()
              .describe('Date and time the reply was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the reply was updated.')
              .optional()
              .nullable(),
          })
          .describe('Reply to the review.')
          .optional(),
        foundHelpful: z
          .number()
          .int()
          .describe('Number of site visitors who found the review helpful.')
          .optional(),
        foundUnhelpful: z
          .number()
          .int()
          .describe('Number of site visitors who found the review unhelpful.')
          .optional(),
        helpfulness: z
          .number()
          .int()
          .describe(
            'Helpfulness score.\n\nCalculated by subtracting `foundUnhelpful` from `foundHelpful`.'
          )
          .optional(),
        moderation: z
          .object({
            moderationStatus: z
              .enum([
                'UNKNOWN',
                'APPROVED',
                'IN_MODERATION',
                'REJECTED',
                'SUBMITTED',
              ])
              .optional(),
            manuallyChanged: z
              .boolean()
              .describe(
                'Indicates whether a moderator manually changed the `moderationStatus` of the review.\n\nIf the `moderationStatus` changed automatically because the review\npassed the moderation rules or if moderation is turned off, this field is set to `false`.'
              )
              .optional(),
          })
          .describe('Moderation status of the review.')
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the review is updated.\nTo prevent conflicting changes,\nthe current `revision` must be passed when updating the review.\nIgnored when creating a review.'
          )
          .optional()
          .nullable(),
        verified: z
          .boolean()
          .describe('Whether the review has been verified.')
          .optional(),
        origin: z
          .object({
            type: z.enum(['UNKNOWN', 'ORGANIC', 'APP']).optional(),
            appId: z
              .string()
              .describe(
                'The app ID of the origin for the review.\nOnly available when the `origin.type` is `"APP"`.'
              )
              .optional()
              .nullable(),
          })
          .describe('Origin of a review.')
          .optional(),
        reviewDate: z
          .date()
          .describe(
            'Date and time when the review was written.\nThis should match `createdDate`, except for reviews imported from another system.'
          )
          .optional()
          .nullable(),
        relevanceScore: z
          .number()
          .describe(
            "Platform-calculated score of review's relevance to the item.\nUsed to sort reviews by relevance."
          )
          .optional()
          .nullable(),
        publishCount: z
          .number()
          .int()
          .describe('Number of times a review has been published.')
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to return the created entities.')
        .optional(),
    })
    .optional(),
});
export const BulkCreateReviewResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('ItemMetadata')
          .optional(),
        review: z
          .object({
            namespace: z
              .string()
              .describe(
                'Review namespace.\n\nSupported values:\n+ Wix Stores: `stores`\n\nCurrently, only Wix Stores is fully integrated with the Wix Reviews app.'
              )
              .optional(),
            entityId: z
              .string()
              .describe(
                'ID of the entity to review. For example, a Wix Stores product ID.'
              )
              .max(36)
              .optional(),
            _id: z
              .string()
              .describe('Review ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            content: z
              .object({
                title: z
                  .string()
                  .describe('Review title.')
                  .max(3000)
                  .optional()
                  .nullable(),
                body: z
                  .string()
                  .describe('Review body.\n\nMax: 3,000 characters')
                  .max(3000)
                  .optional()
                  .nullable(),
                media: z
                  .array(
                    z.intersection(
                      z.object({}),
                      z.xor([
                        z.object({
                          image: z.never().optional(),
                          video: z.never().optional(),
                        }),
                        z.object({
                          video: z.never().optional(),
                          image: z.string().describe('Image media item.'),
                        }),
                        z.object({
                          image: z.never().optional(),
                          video: z.string().describe('Video media item.'),
                        }),
                      ])
                    )
                  )
                  .max(10)
                  .optional(),
                rating: z
                  .number()
                  .int()
                  .describe(
                    'Rating of the review.\n\nMin: `1` <br />\nMax: `5`'
                  )
                  .optional(),
              })
              .describe('Review content.')
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the review was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the review was updated.')
              .optional()
              .nullable(),
            author: z
              .object({
                contactId: z
                  .string()
                  .describe('Contact ID of the author.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                authorName: z
                  .string()
                  .describe('Display name of the author.')
                  .max(100)
                  .optional()
                  .nullable(),
              })
              .describe('Author of the review.')
              .optional(),
            reply: z
              .object({
                message: z
                  .string()
                  .describe('Reply content.')
                  .max(1000)
                  .optional(),
                _createdDate: z
                  .date()
                  .describe('Date and time the reply was created.')
                  .optional()
                  .nullable(),
                _updatedDate: z
                  .date()
                  .describe('Date and time the reply was updated.')
                  .optional()
                  .nullable(),
              })
              .describe('Reply to the review.')
              .optional(),
            foundHelpful: z
              .number()
              .int()
              .describe('Number of site visitors who found the review helpful.')
              .optional(),
            foundUnhelpful: z
              .number()
              .int()
              .describe(
                'Number of site visitors who found the review unhelpful.'
              )
              .optional(),
            helpfulness: z
              .number()
              .int()
              .describe(
                'Helpfulness score.\n\nCalculated by subtracting `foundUnhelpful` from `foundHelpful`.'
              )
              .optional(),
            moderation: z
              .object({
                moderationStatus: z
                  .enum([
                    'UNKNOWN',
                    'APPROVED',
                    'IN_MODERATION',
                    'REJECTED',
                    'SUBMITTED',
                  ])
                  .optional(),
                manuallyChanged: z
                  .boolean()
                  .describe(
                    'Indicates whether a moderator manually changed the `moderationStatus` of the review.\n\nIf the `moderationStatus` changed automatically because the review\npassed the moderation rules or if moderation is turned off, this field is set to `false`.'
                  )
                  .optional(),
              })
              .describe('Moderation status of the review.')
              .optional(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the review is updated.\nTo prevent conflicting changes,\nthe current `revision` must be passed when updating the review.\nIgnored when creating a review.'
              )
              .optional()
              .nullable(),
            verified: z
              .boolean()
              .describe('Whether the review has been verified.')
              .optional(),
            origin: z
              .object({
                type: z
                  .enum(['UNKNOWN', 'ORGANIC', 'APP'])
                  .describe('Origin type.')
                  .optional(),
                appId: z
                  .string()
                  .describe(
                    'The app ID of the origin for the review.\nOnly available when the `origin.type` is `"APP"`.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Origin of a review.')
              .optional(),
            reviewDate: z
              .date()
              .describe(
                'Date and time when the review was written.\nThis should match `createdDate`, except for reviews imported from another system.'
              )
              .optional()
              .nullable(),
            relevanceScore: z
              .number()
              .describe(
                "Platform-calculated score of review's relevance to the item.\nUsed to sort reviews by relevance."
              )
              .optional()
              .nullable(),
            publishCount: z
              .number()
              .int()
              .describe('Number of times a review has been published.')
              .optional(),
          })
          .describe('New Reviews')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const UpdateReviewRequest = z.object({
  _id: z
    .string()
    .describe('Review ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  review: z
    .object({
      namespace: z
        .string()
        .describe(
          'Review namespace.\n\nSupported values:\n+ Wix Stores: `stores`\n\nCurrently, only Wix Stores is fully integrated with the Wix Reviews app.'
        )
        .optional(),
      entityId: z
        .string()
        .describe(
          'ID of the entity to review. For example, a Wix Stores product ID.'
        )
        .max(36)
        .optional(),
      _id: z
        .string()
        .describe('Review ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      content: z
        .object({
          title: z
            .string()
            .describe('Review title.')
            .max(3000)
            .optional()
            .nullable(),
          body: z
            .string()
            .describe('Review body.\n\nMax: 3,000 characters')
            .max(3000)
            .optional()
            .nullable(),
          media: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                  }),
                  z.object({
                    video: z.never().optional(),
                    image: z.string().describe('Image media item.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.string().describe('Video media item.'),
                  }),
                ])
              )
            )
            .max(10)
            .optional(),
          rating: z
            .number()
            .int()
            .describe('Rating of the review.\n\nMin: `1` <br />\nMax: `5`')
            .optional(),
        })
        .describe('Review content.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the review was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the review was updated.')
        .optional()
        .nullable(),
      author: z
        .object({
          contactId: z
            .string()
            .describe('Contact ID of the author.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          authorName: z
            .string()
            .describe('Display name of the author.')
            .max(100)
            .optional()
            .nullable(),
        })
        .describe('Author of the review.')
        .optional(),
      reply: z
        .object({
          message: z.string().describe('Reply content.').max(1000).optional(),
          _createdDate: z
            .date()
            .describe('Date and time the reply was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the reply was updated.')
            .optional()
            .nullable(),
        })
        .describe('Reply to the review.')
        .optional(),
      foundHelpful: z
        .number()
        .int()
        .describe('Number of site visitors who found the review helpful.')
        .optional(),
      foundUnhelpful: z
        .number()
        .int()
        .describe('Number of site visitors who found the review unhelpful.')
        .optional(),
      helpfulness: z
        .number()
        .int()
        .describe(
          'Helpfulness score.\n\nCalculated by subtracting `foundUnhelpful` from `foundHelpful`.'
        )
        .optional(),
      moderation: z
        .object({
          moderationStatus: z
            .enum([
              'UNKNOWN',
              'APPROVED',
              'IN_MODERATION',
              'REJECTED',
              'SUBMITTED',
            ])
            .optional(),
          manuallyChanged: z
            .boolean()
            .describe(
              'Indicates whether a moderator manually changed the `moderationStatus` of the review.\n\nIf the `moderationStatus` changed automatically because the review\npassed the moderation rules or if moderation is turned off, this field is set to `false`.'
            )
            .optional(),
        })
        .describe('Moderation status of the review.')
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the review is updated.\nTo prevent conflicting changes,\nthe current `revision` must be passed when updating the review.\nIgnored when creating a review.'
        ),
      verified: z
        .boolean()
        .describe('Whether the review has been verified.')
        .optional(),
      origin: z
        .object({
          type: z.enum(['UNKNOWN', 'ORGANIC', 'APP']).optional(),
          appId: z
            .string()
            .describe(
              'The app ID of the origin for the review.\nOnly available when the `origin.type` is `"APP"`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Origin of a review.')
        .optional(),
      reviewDate: z
        .date()
        .describe(
          'Date and time when the review was written.\nThis should match `createdDate`, except for reviews imported from another system.'
        )
        .optional()
        .nullable(),
      relevanceScore: z
        .number()
        .describe(
          "Platform-calculated score of review's relevance to the item.\nUsed to sort reviews by relevance."
        )
        .optional()
        .nullable(),
      publishCount: z
        .number()
        .int()
        .describe('Number of times a review has been published.')
        .optional(),
    })
    .describe('Review to update.'),
});
export const UpdateReviewResponse = z.object({
  namespace: z
    .string()
    .describe(
      'Review namespace.\n\nSupported values:\n+ Wix Stores: `stores`\n\nCurrently, only Wix Stores is fully integrated with the Wix Reviews app.'
    )
    .optional(),
  entityId: z
    .string()
    .describe(
      'ID of the entity to review. For example, a Wix Stores product ID.'
    )
    .max(36)
    .optional(),
  _id: z
    .string()
    .describe('Review ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  content: z
    .object({
      title: z
        .string()
        .describe('Review title.')
        .max(3000)
        .optional()
        .nullable(),
      body: z
        .string()
        .describe('Review body.\n\nMax: 3,000 characters')
        .max(3000)
        .optional()
        .nullable(),
      media: z
        .array(
          z.intersection(
            z.object({}),
            z.xor([
              z.object({
                image: z.never().optional(),
                video: z.never().optional(),
              }),
              z.object({
                video: z.never().optional(),
                image: z.string().describe('Image media item.'),
              }),
              z.object({
                image: z.never().optional(),
                video: z.string().describe('Video media item.'),
              }),
            ])
          )
        )
        .max(10)
        .optional(),
      rating: z
        .number()
        .int()
        .describe('Rating of the review.\n\nMin: `1` <br />\nMax: `5`')
        .optional(),
    })
    .describe('Review content.')
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time the review was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the review was updated.')
    .optional()
    .nullable(),
  author: z
    .object({
      contactId: z
        .string()
        .describe('Contact ID of the author.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      authorName: z
        .string()
        .describe('Display name of the author.')
        .max(100)
        .optional()
        .nullable(),
    })
    .describe('Author of the review.')
    .optional(),
  reply: z
    .object({
      message: z.string().describe('Reply content.').max(1000).optional(),
      _createdDate: z
        .date()
        .describe('Date and time the reply was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the reply was updated.')
        .optional()
        .nullable(),
    })
    .describe('Reply to the review.')
    .optional(),
  foundHelpful: z
    .number()
    .int()
    .describe('Number of site visitors who found the review helpful.')
    .optional(),
  foundUnhelpful: z
    .number()
    .int()
    .describe('Number of site visitors who found the review unhelpful.')
    .optional(),
  helpfulness: z
    .number()
    .int()
    .describe(
      'Helpfulness score.\n\nCalculated by subtracting `foundUnhelpful` from `foundHelpful`.'
    )
    .optional(),
  moderation: z
    .object({
      moderationStatus: z
        .enum(['UNKNOWN', 'APPROVED', 'IN_MODERATION', 'REJECTED', 'SUBMITTED'])
        .optional(),
      manuallyChanged: z
        .boolean()
        .describe(
          'Indicates whether a moderator manually changed the `moderationStatus` of the review.\n\nIf the `moderationStatus` changed automatically because the review\npassed the moderation rules or if moderation is turned off, this field is set to `false`.'
        )
        .optional(),
    })
    .describe('Moderation status of the review.')
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the review is updated.\nTo prevent conflicting changes,\nthe current `revision` must be passed when updating the review.\nIgnored when creating a review.'
    )
    .optional()
    .nullable(),
  verified: z
    .boolean()
    .describe('Whether the review has been verified.')
    .optional(),
  origin: z
    .object({
      type: z
        .enum(['UNKNOWN', 'ORGANIC', 'APP'])
        .describe('Origin type.')
        .optional(),
      appId: z
        .string()
        .describe(
          'The app ID of the origin for the review.\nOnly available when the `origin.type` is `"APP"`.'
        )
        .optional()
        .nullable(),
    })
    .describe('Origin of a review.')
    .optional(),
  reviewDate: z
    .date()
    .describe(
      'Date and time when the review was written.\nThis should match `createdDate`, except for reviews imported from another system.'
    )
    .optional()
    .nullable(),
  relevanceScore: z
    .number()
    .describe(
      "Platform-calculated score of review's relevance to the item.\nUsed to sort reviews by relevance."
    )
    .optional()
    .nullable(),
  publishCount: z
    .number()
    .int()
    .describe('Number of times a review has been published.')
    .optional(),
});
export const DeleteReviewRequest = z.object({
  reviewId: z
    .string()
    .describe('Review ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteReviewResponse = z.object({
  review: z
    .object({
      namespace: z
        .string()
        .describe(
          'Review namespace.\n\nSupported values:\n+ Wix Stores: `stores`\n\nCurrently, only Wix Stores is fully integrated with the Wix Reviews app.'
        )
        .optional(),
      entityId: z
        .string()
        .describe(
          'ID of the entity to review. For example, a Wix Stores product ID.'
        )
        .max(36)
        .optional(),
      _id: z
        .string()
        .describe('Review ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      content: z
        .object({
          title: z
            .string()
            .describe('Review title.')
            .max(3000)
            .optional()
            .nullable(),
          body: z
            .string()
            .describe('Review body.\n\nMax: 3,000 characters')
            .max(3000)
            .optional()
            .nullable(),
          media: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                  }),
                  z.object({
                    video: z.never().optional(),
                    image: z.string().describe('Image media item.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.string().describe('Video media item.'),
                  }),
                ])
              )
            )
            .max(10)
            .optional(),
          rating: z
            .number()
            .int()
            .describe('Rating of the review.\n\nMin: `1` <br />\nMax: `5`')
            .optional(),
        })
        .describe('Review content.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the review was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the review was updated.')
        .optional()
        .nullable(),
      author: z
        .object({
          contactId: z
            .string()
            .describe('Contact ID of the author.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          authorName: z
            .string()
            .describe('Display name of the author.')
            .max(100)
            .optional()
            .nullable(),
        })
        .describe('Author of the review.')
        .optional(),
      reply: z
        .object({
          message: z.string().describe('Reply content.').max(1000).optional(),
          _createdDate: z
            .date()
            .describe('Date and time the reply was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the reply was updated.')
            .optional()
            .nullable(),
        })
        .describe('Reply to the review.')
        .optional(),
      foundHelpful: z
        .number()
        .int()
        .describe('Number of site visitors who found the review helpful.')
        .optional(),
      foundUnhelpful: z
        .number()
        .int()
        .describe('Number of site visitors who found the review unhelpful.')
        .optional(),
      helpfulness: z
        .number()
        .int()
        .describe(
          'Helpfulness score.\n\nCalculated by subtracting `foundUnhelpful` from `foundHelpful`.'
        )
        .optional(),
      moderation: z
        .object({
          moderationStatus: z
            .enum([
              'UNKNOWN',
              'APPROVED',
              'IN_MODERATION',
              'REJECTED',
              'SUBMITTED',
            ])
            .optional(),
          manuallyChanged: z
            .boolean()
            .describe(
              'Indicates whether a moderator manually changed the `moderationStatus` of the review.\n\nIf the `moderationStatus` changed automatically because the review\npassed the moderation rules or if moderation is turned off, this field is set to `false`.'
            )
            .optional(),
        })
        .describe('Moderation status of the review.')
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the review is updated.\nTo prevent conflicting changes,\nthe current `revision` must be passed when updating the review.\nIgnored when creating a review.'
        )
        .optional()
        .nullable(),
      verified: z
        .boolean()
        .describe('Whether the review has been verified.')
        .optional(),
      origin: z
        .object({
          type: z
            .enum(['UNKNOWN', 'ORGANIC', 'APP'])
            .describe('Origin type.')
            .optional(),
          appId: z
            .string()
            .describe(
              'The app ID of the origin for the review.\nOnly available when the `origin.type` is `"APP"`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Origin of a review.')
        .optional(),
      reviewDate: z
        .date()
        .describe(
          'Date and time when the review was written.\nThis should match `createdDate`, except for reviews imported from another system.'
        )
        .optional()
        .nullable(),
      relevanceScore: z
        .number()
        .describe(
          "Platform-calculated score of review's relevance to the item.\nUsed to sort reviews by relevance."
        )
        .optional()
        .nullable(),
      publishCount: z
        .number()
        .int()
        .describe('Number of times a review has been published.')
        .optional(),
    })
    .describe('Review entity.')
    .optional(),
});
export const BulkDeleteReviewsRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Query options. See [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language) for more details.'
        )
        .optional()
        .nullable(),
    })
    .describe('Information about the reviews to delete.')
    .optional(),
});
export const BulkDeleteReviewsResponse = z.object({
  jobId: z.string().describe('Bulk job ID.').optional(),
});
export const QueryReviewsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          namespace: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'moderation.moderationStatus': z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          entityId: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'author.contactId': z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          relevanceScore: z
            .object({
              $eq: z.number(),
              $gt: z.number(),
              $gte: z.number(),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
            })
            .partial()
            .strict()
            .optional(),
          'content.rating': z
            .object({
              $eq: z.number(),
              $gt: z.number(),
              $gte: z.number(),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
            })
            .partial()
            .strict()
            .optional(),
          helpfulness: z
            .object({
              $eq: z.number(),
              $gt: z.number(),
              $gte: z.number(),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          verified: z
            .object({ $eq: z.boolean(), $ne: z.boolean() })
            .partial()
            .strict()
            .optional(),
          'origin.type': z
            .object({ $eq: z.string(), $ne: z.string() })
            .partial()
            .strict()
            .optional(),
          'content.media': z.object({}).partial().strict().optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                'relevanceScore',
                'content.rating',
                'helpfulness',
                '_createdDate',
                'verified',
                'origin.type',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Review query.'),
  options: z
    .object({
      returnPrivateReviews: z
        .boolean()
        .describe(
          'Whether to return private (unpublished) reviews.\n\nDefault: `false`'
        )
        .optional(),
    })
    .describe('Information about the reviews to retrieve.')
    .optional(),
});
export const QueryReviewsResponse = z.object({
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          "Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag isn't set."
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next result page.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe('Cursor pointing to previous result page.')
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata.')
    .optional(),
  reviews: z
    .array(
      z.object({
        namespace: z
          .string()
          .describe(
            'Review namespace.\n\nSupported values:\n+ Wix Stores: `stores`\n\nCurrently, only Wix Stores is fully integrated with the Wix Reviews app.'
          )
          .optional(),
        entityId: z
          .string()
          .describe(
            'ID of the entity to review. For example, a Wix Stores product ID.'
          )
          .max(36)
          .optional(),
        _id: z
          .string()
          .describe('Review ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        content: z
          .object({
            title: z
              .string()
              .describe('Review title.')
              .max(3000)
              .optional()
              .nullable(),
            body: z
              .string()
              .describe('Review body.\n\nMax: 3,000 characters')
              .max(3000)
              .optional()
              .nullable(),
            media: z
              .array(
                z.intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      image: z.never().optional(),
                      video: z.never().optional(),
                    }),
                    z.object({
                      video: z.never().optional(),
                      image: z.string().describe('Image media item.'),
                    }),
                    z.object({
                      image: z.never().optional(),
                      video: z.string().describe('Video media item.'),
                    }),
                  ])
                )
              )
              .max(10)
              .optional(),
            rating: z
              .number()
              .int()
              .describe('Rating of the review.\n\nMin: `1` <br />\nMax: `5`')
              .optional(),
          })
          .describe('Review content.')
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the review was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the review was updated.')
          .optional()
          .nullable(),
        author: z
          .object({
            contactId: z
              .string()
              .describe('Contact ID of the author.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            authorName: z
              .string()
              .describe('Display name of the author.')
              .max(100)
              .optional()
              .nullable(),
          })
          .describe('Author of the review.')
          .optional(),
        reply: z
          .object({
            message: z.string().describe('Reply content.').max(1000).optional(),
            _createdDate: z
              .date()
              .describe('Date and time the reply was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the reply was updated.')
              .optional()
              .nullable(),
          })
          .describe('Reply to the review.')
          .optional(),
        foundHelpful: z
          .number()
          .int()
          .describe('Number of site visitors who found the review helpful.')
          .optional(),
        foundUnhelpful: z
          .number()
          .int()
          .describe('Number of site visitors who found the review unhelpful.')
          .optional(),
        helpfulness: z
          .number()
          .int()
          .describe(
            'Helpfulness score.\n\nCalculated by subtracting `foundUnhelpful` from `foundHelpful`.'
          )
          .optional(),
        moderation: z
          .object({
            moderationStatus: z
              .enum([
                'UNKNOWN',
                'APPROVED',
                'IN_MODERATION',
                'REJECTED',
                'SUBMITTED',
              ])
              .optional(),
            manuallyChanged: z
              .boolean()
              .describe(
                'Indicates whether a moderator manually changed the `moderationStatus` of the review.\n\nIf the `moderationStatus` changed automatically because the review\npassed the moderation rules or if moderation is turned off, this field is set to `false`.'
              )
              .optional(),
          })
          .describe('Moderation status of the review.')
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the review is updated.\nTo prevent conflicting changes,\nthe current `revision` must be passed when updating the review.\nIgnored when creating a review.'
          )
          .optional()
          .nullable(),
        verified: z
          .boolean()
          .describe('Whether the review has been verified.')
          .optional(),
        origin: z
          .object({
            type: z
              .enum(['UNKNOWN', 'ORGANIC', 'APP'])
              .describe('Origin type.')
              .optional(),
            appId: z
              .string()
              .describe(
                'The app ID of the origin for the review.\nOnly available when the `origin.type` is `"APP"`.'
              )
              .optional()
              .nullable(),
          })
          .describe('Origin of a review.')
          .optional(),
        reviewDate: z
          .date()
          .describe(
            'Date and time when the review was written.\nThis should match `createdDate`, except for reviews imported from another system.'
          )
          .optional()
          .nullable(),
        relevanceScore: z
          .number()
          .describe(
            "Platform-calculated score of review's relevance to the item.\nUsed to sort reviews by relevance."
          )
          .optional()
          .nullable(),
        publishCount: z
          .number()
          .int()
          .describe('Number of times a review has been published.')
          .optional(),
      })
    )
    .optional(),
});
export const SetReplyRequest = z.object({
  reviewId: z
    .string()
    .describe('Review ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  message: z.string().describe('Response to review author.').max(10000),
});
export const SetReplyResponse = z.object({
  review: z
    .object({
      namespace: z
        .string()
        .describe(
          'Review namespace.\n\nSupported values:\n+ Wix Stores: `stores`\n\nCurrently, only Wix Stores is fully integrated with the Wix Reviews app.'
        )
        .optional(),
      entityId: z
        .string()
        .describe(
          'ID of the entity to review. For example, a Wix Stores product ID.'
        )
        .max(36)
        .optional(),
      _id: z
        .string()
        .describe('Review ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      content: z
        .object({
          title: z
            .string()
            .describe('Review title.')
            .max(3000)
            .optional()
            .nullable(),
          body: z
            .string()
            .describe('Review body.\n\nMax: 3,000 characters')
            .max(3000)
            .optional()
            .nullable(),
          media: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                  }),
                  z.object({
                    video: z.never().optional(),
                    image: z.string().describe('Image media item.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.string().describe('Video media item.'),
                  }),
                ])
              )
            )
            .max(10)
            .optional(),
          rating: z
            .number()
            .int()
            .describe('Rating of the review.\n\nMin: `1` <br />\nMax: `5`')
            .optional(),
        })
        .describe('Review content.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the review was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the review was updated.')
        .optional()
        .nullable(),
      author: z
        .object({
          contactId: z
            .string()
            .describe('Contact ID of the author.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          authorName: z
            .string()
            .describe('Display name of the author.')
            .max(100)
            .optional()
            .nullable(),
        })
        .describe('Author of the review.')
        .optional(),
      reply: z
        .object({
          message: z.string().describe('Reply content.').max(1000).optional(),
          _createdDate: z
            .date()
            .describe('Date and time the reply was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the reply was updated.')
            .optional()
            .nullable(),
        })
        .describe('Reply to the review.')
        .optional(),
      foundHelpful: z
        .number()
        .int()
        .describe('Number of site visitors who found the review helpful.')
        .optional(),
      foundUnhelpful: z
        .number()
        .int()
        .describe('Number of site visitors who found the review unhelpful.')
        .optional(),
      helpfulness: z
        .number()
        .int()
        .describe(
          'Helpfulness score.\n\nCalculated by subtracting `foundUnhelpful` from `foundHelpful`.'
        )
        .optional(),
      moderation: z
        .object({
          moderationStatus: z
            .enum([
              'UNKNOWN',
              'APPROVED',
              'IN_MODERATION',
              'REJECTED',
              'SUBMITTED',
            ])
            .optional(),
          manuallyChanged: z
            .boolean()
            .describe(
              'Indicates whether a moderator manually changed the `moderationStatus` of the review.\n\nIf the `moderationStatus` changed automatically because the review\npassed the moderation rules or if moderation is turned off, this field is set to `false`.'
            )
            .optional(),
        })
        .describe('Moderation status of the review.')
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the review is updated.\nTo prevent conflicting changes,\nthe current `revision` must be passed when updating the review.\nIgnored when creating a review.'
        )
        .optional()
        .nullable(),
      verified: z
        .boolean()
        .describe('Whether the review has been verified.')
        .optional(),
      origin: z
        .object({
          type: z
            .enum(['UNKNOWN', 'ORGANIC', 'APP'])
            .describe('Origin type.')
            .optional(),
          appId: z
            .string()
            .describe(
              'The app ID of the origin for the review.\nOnly available when the `origin.type` is `"APP"`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Origin of a review.')
        .optional(),
      reviewDate: z
        .date()
        .describe(
          'Date and time when the review was written.\nThis should match `createdDate`, except for reviews imported from another system.'
        )
        .optional()
        .nullable(),
      relevanceScore: z
        .number()
        .describe(
          "Platform-calculated score of review's relevance to the item.\nUsed to sort reviews by relevance."
        )
        .optional()
        .nullable(),
      publishCount: z
        .number()
        .int()
        .describe('Number of times a review has been published.')
        .optional(),
    })
    .describe('Updated review.')
    .optional(),
});
export const RemoveReplyRequest = z.object({
  reviewId: z
    .string()
    .describe('ID of review to remove the reply for.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const RemoveReplyResponse = z.object({
  review: z
    .object({
      namespace: z
        .string()
        .describe(
          'Review namespace.\n\nSupported values:\n+ Wix Stores: `stores`\n\nCurrently, only Wix Stores is fully integrated with the Wix Reviews app.'
        )
        .optional(),
      entityId: z
        .string()
        .describe(
          'ID of the entity to review. For example, a Wix Stores product ID.'
        )
        .max(36)
        .optional(),
      _id: z
        .string()
        .describe('Review ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      content: z
        .object({
          title: z
            .string()
            .describe('Review title.')
            .max(3000)
            .optional()
            .nullable(),
          body: z
            .string()
            .describe('Review body.\n\nMax: 3,000 characters')
            .max(3000)
            .optional()
            .nullable(),
          media: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                  }),
                  z.object({
                    video: z.never().optional(),
                    image: z.string().describe('Image media item.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.string().describe('Video media item.'),
                  }),
                ])
              )
            )
            .max(10)
            .optional(),
          rating: z
            .number()
            .int()
            .describe('Rating of the review.\n\nMin: `1` <br />\nMax: `5`')
            .optional(),
        })
        .describe('Review content.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the review was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the review was updated.')
        .optional()
        .nullable(),
      author: z
        .object({
          contactId: z
            .string()
            .describe('Contact ID of the author.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          authorName: z
            .string()
            .describe('Display name of the author.')
            .max(100)
            .optional()
            .nullable(),
        })
        .describe('Author of the review.')
        .optional(),
      reply: z
        .object({
          message: z.string().describe('Reply content.').max(1000).optional(),
          _createdDate: z
            .date()
            .describe('Date and time the reply was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the reply was updated.')
            .optional()
            .nullable(),
        })
        .describe('Reply to the review.')
        .optional(),
      foundHelpful: z
        .number()
        .int()
        .describe('Number of site visitors who found the review helpful.')
        .optional(),
      foundUnhelpful: z
        .number()
        .int()
        .describe('Number of site visitors who found the review unhelpful.')
        .optional(),
      helpfulness: z
        .number()
        .int()
        .describe(
          'Helpfulness score.\n\nCalculated by subtracting `foundUnhelpful` from `foundHelpful`.'
        )
        .optional(),
      moderation: z
        .object({
          moderationStatus: z
            .enum([
              'UNKNOWN',
              'APPROVED',
              'IN_MODERATION',
              'REJECTED',
              'SUBMITTED',
            ])
            .optional(),
          manuallyChanged: z
            .boolean()
            .describe(
              'Indicates whether a moderator manually changed the `moderationStatus` of the review.\n\nIf the `moderationStatus` changed automatically because the review\npassed the moderation rules or if moderation is turned off, this field is set to `false`.'
            )
            .optional(),
        })
        .describe('Moderation status of the review.')
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the review is updated.\nTo prevent conflicting changes,\nthe current `revision` must be passed when updating the review.\nIgnored when creating a review.'
        )
        .optional()
        .nullable(),
      verified: z
        .boolean()
        .describe('Whether the review has been verified.')
        .optional(),
      origin: z
        .object({
          type: z
            .enum(['UNKNOWN', 'ORGANIC', 'APP'])
            .describe('Origin type.')
            .optional(),
          appId: z
            .string()
            .describe(
              'The app ID of the origin for the review.\nOnly available when the `origin.type` is `"APP"`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Origin of a review.')
        .optional(),
      reviewDate: z
        .date()
        .describe(
          'Date and time when the review was written.\nThis should match `createdDate`, except for reviews imported from another system.'
        )
        .optional()
        .nullable(),
      relevanceScore: z
        .number()
        .describe(
          "Platform-calculated score of review's relevance to the item.\nUsed to sort reviews by relevance."
        )
        .optional()
        .nullable(),
      publishCount: z
        .number()
        .int()
        .describe('Number of times a review has been published.')
        .optional(),
    })
    .describe('Updated review.')
    .optional(),
});
export const UpdateModerationStatusRequest = z.object({
  reviewId: z
    .string()
    .describe('ID of the review to moderate.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      status: z
        .enum(['UNKNOWN_MODERATION_STATUS', 'APPROVED', 'REJECTED'])
        .optional(),
    })
    .optional(),
});
export const UpdateModerationStatusResponse = z.object({
  review: z
    .object({
      namespace: z
        .string()
        .describe(
          'Review namespace.\n\nSupported values:\n+ Wix Stores: `stores`\n\nCurrently, only Wix Stores is fully integrated with the Wix Reviews app.'
        )
        .optional(),
      entityId: z
        .string()
        .describe(
          'ID of the entity to review. For example, a Wix Stores product ID.'
        )
        .max(36)
        .optional(),
      _id: z
        .string()
        .describe('Review ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      content: z
        .object({
          title: z
            .string()
            .describe('Review title.')
            .max(3000)
            .optional()
            .nullable(),
          body: z
            .string()
            .describe('Review body.\n\nMax: 3,000 characters')
            .max(3000)
            .optional()
            .nullable(),
          media: z
            .array(
              z.intersection(
                z.object({}),
                z.xor([
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                  }),
                  z.object({
                    video: z.never().optional(),
                    image: z.string().describe('Image media item.'),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z.string().describe('Video media item.'),
                  }),
                ])
              )
            )
            .max(10)
            .optional(),
          rating: z
            .number()
            .int()
            .describe('Rating of the review.\n\nMin: `1` <br />\nMax: `5`')
            .optional(),
        })
        .describe('Review content.')
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the review was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the review was updated.')
        .optional()
        .nullable(),
      author: z
        .object({
          contactId: z
            .string()
            .describe('Contact ID of the author.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          authorName: z
            .string()
            .describe('Display name of the author.')
            .max(100)
            .optional()
            .nullable(),
        })
        .describe('Author of the review.')
        .optional(),
      reply: z
        .object({
          message: z.string().describe('Reply content.').max(1000).optional(),
          _createdDate: z
            .date()
            .describe('Date and time the reply was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the reply was updated.')
            .optional()
            .nullable(),
        })
        .describe('Reply to the review.')
        .optional(),
      foundHelpful: z
        .number()
        .int()
        .describe('Number of site visitors who found the review helpful.')
        .optional(),
      foundUnhelpful: z
        .number()
        .int()
        .describe('Number of site visitors who found the review unhelpful.')
        .optional(),
      helpfulness: z
        .number()
        .int()
        .describe(
          'Helpfulness score.\n\nCalculated by subtracting `foundUnhelpful` from `foundHelpful`.'
        )
        .optional(),
      moderation: z
        .object({
          moderationStatus: z
            .enum([
              'UNKNOWN',
              'APPROVED',
              'IN_MODERATION',
              'REJECTED',
              'SUBMITTED',
            ])
            .optional(),
          manuallyChanged: z
            .boolean()
            .describe(
              'Indicates whether a moderator manually changed the `moderationStatus` of the review.\n\nIf the `moderationStatus` changed automatically because the review\npassed the moderation rules or if moderation is turned off, this field is set to `false`.'
            )
            .optional(),
        })
        .describe('Moderation status of the review.')
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the review is updated.\nTo prevent conflicting changes,\nthe current `revision` must be passed when updating the review.\nIgnored when creating a review.'
        )
        .optional()
        .nullable(),
      verified: z
        .boolean()
        .describe('Whether the review has been verified.')
        .optional(),
      origin: z
        .object({
          type: z
            .enum(['UNKNOWN', 'ORGANIC', 'APP'])
            .describe('Origin type.')
            .optional(),
          appId: z
            .string()
            .describe(
              'The app ID of the origin for the review.\nOnly available when the `origin.type` is `"APP"`.'
            )
            .optional()
            .nullable(),
        })
        .describe('Origin of a review.')
        .optional(),
      reviewDate: z
        .date()
        .describe(
          'Date and time when the review was written.\nThis should match `createdDate`, except for reviews imported from another system.'
        )
        .optional()
        .nullable(),
      relevanceScore: z
        .number()
        .describe(
          "Platform-calculated score of review's relevance to the item.\nUsed to sort reviews by relevance."
        )
        .optional()
        .nullable(),
      publishCount: z
        .number()
        .int()
        .describe('Number of times a review has been published.')
        .optional(),
    })
    .describe('The review after moderation.')
    .optional(),
});
export const BulkUpdateModerationStatusRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe('Reviews to moderate.')
        .optional()
        .nullable(),
      status: z.enum(['UNKNOWN', 'APPROVED', 'REJECTED']).optional(),
    })
    .optional(),
});
export const BulkUpdateModerationStatusResponse = z.object({
  jobId: z.string().describe('Job ID.').optional(),
});
export const CountReviewsRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\n\nSee [Query Reviews](https://dev.wix.com/docs/rest/crm/community/reviews/reviews/query-reviews) for a list of supported filters.'
        )
        .optional()
        .nullable(),
      includePrivateReviews: z
        .boolean()
        .describe(
          'Whether to include unpublished reviews in the count.\n\nDefault: `false`'
        )
        .optional(),
    })
    .optional(),
});
export const CountReviewsResponse = z.object({
  count: z.number().int().describe('Number of reviews.').optional(),
});
